import * as React from "react";
import { useLocation } from "react-router-dom";
import lohoWhite from "../../assets/icons/loho-white.svg";
import { ListOfMenus } from "../../utils/AuthorizationManager";
import AppSubmenu from "./AppSubmenu";
import LeadsSecondaryPanel from "./LeadsSecondaryPanel";
import {
  drawerWidthCollapsed,
  drawerWidthExpanded,
  subMenuWidth,
} from "../../utils/styles";
import history from "../../utils/history";
import { canShowLeafItem } from "./menuPrivileges";

interface Props {
  isActive?: boolean;
  open?: boolean;
  setOpen: Function;
  window?: () => Window;
  onSidebarWidthChange?: (width: number) => void;
}

const normalizePath = (path: string) => {
  if (!path) return "";
  const cleaned = path.split("?")[0].replace(/\/+$/, "");
  return cleaned || "/";
};

const isComposerPath = (pathname: string) =>
  /\/(all-leads|hometatva-leads|my-Lead|team|archive-member|leadform)\/(add|edit)(\/|$)|\/project\/create(\/|$)|\/marketing\/whatsapp\/(create|[^/]+)$/i.test(
    normalizePath(pathname)
  );

const linkMatches = (link: string | undefined, pathname: string) => {
  if (!link) return false;
  const target = normalizePath(link);
  const current = normalizePath(pathname);
  return current === target || current.startsWith(`${target}/`);
};

const menuHasActiveChild = (item: any, pathname: string): boolean => {
  if (!item?.subMenu?.length) return false;
  return item.subMenu.some((menu: any) => {
    if (menu?.link && linkMatches(menu.link, pathname)) return true;
    return (menu?.subSubMenu || []).some((child: any) =>
      linkMatches(child?.link, pathname)
    );
  });
};

const firstPrivilegedLeafLink = (menu: any): string | null => {
  for (const group of menu?.subMenu || []) {
    for (const leaf of group?.subSubMenu || []) {
      if (canShowLeafItem(leaf, menu) && leaf?.link) return leaf.link;
    }
    if (group?.link && canShowLeafItem(group, menu)) return group.link;
  }
  return null;
};

export default function AppDrawer({
  open = true,
  setOpen,
  onSidebarWidthChange,
}: Props) {
  const expanded = open;
  const primaryWidth = expanded ? drawerWidthExpanded : drawerWidthCollapsed;
  const location = useLocation();
  const pathname = location?.pathname || "";

  const routeMenu = React.useMemo(
    () =>
      ListOfMenus.find(
        (m: any) => m?.subMenu?.length && menuHasActiveChild(m, pathname)
      ) || null,
    [pathname]
  );

  const [secondaryMenu, setSecondaryMenu] = React.useState<any | null>(
    routeMenu
  );

  React.useEffect(() => {
    if (isComposerPath(pathname)) {
      setSecondaryMenu(null);
      return;
    }
    setSecondaryMenu(routeMenu);
  }, [routeMenu, pathname]);

  const secondaryOpen = Boolean(secondaryMenu?.subMenu?.length);
  const totalWidth = primaryWidth + (secondaryOpen ? subMenuWidth : 0);

  React.useEffect(() => {
    onSidebarWidthChange?.(totalWidth);
  }, [totalWidth, onSidebarWidthChange]);

  const handleMenuClick = (menu: any) => {
    if (menu?.subMenu?.length > 0) {
      setSecondaryMenu(menu);
      if (isComposerPath(pathname) || !menuHasActiveChild(menu, pathname)) {
        const link = firstPrivilegedLeafLink(menu);
        if (link) history.push(link);
      }
      return;
    }
    setSecondaryMenu(null);
    if (menu?.link) history.push(menu.link);
  };

  return (
    <nav
      aria-label="Main navigation"
      className="hidden shrink-0 sm:block"
      style={{ width: totalWidth, transition: "width 0.2s ease" }}
    >
      <aside
        className="fixed left-0 top-0 z-[1200] flex h-screen flex-col"
        style={{
          width: primaryWidth,
          transition: "width 0.2s ease",
          background: "#841C39",
        }}
      >
        <div
          className={`flex h-[64px] shrink-0 items-center border-b border-white/10 ${
            expanded ? "gap-2.5 px-4" : "justify-center px-2"
          }`}
        >
          <img src={lohoWhite} alt="Pulse CRM" className="h-7 w-7 shrink-0" />
          {expanded && (
            <span className="truncate text-[15px] font-semibold tracking-tight text-white">
              Pulse CRM
            </span>
          )}
        </div>

        <ul className="m-0 flex flex-1 list-none flex-col gap-0.5 overflow-y-auto px-2.5 py-3 [scrollbar-width:thin]">
          {ListOfMenus.filter((e: any) => !e._stickyBottom).map((e: any) => (
            <AppSubmenu
              data={e}
              key={`${e?.pageName}-${e?.key}`}
              collapsed={!expanded}
              secondaryOpen={secondaryMenu?.key === e?.key}
              onMenuClick={handleMenuClick}
            />
          ))}
        </ul>

        <div className="shrink-0 border-t border-white/10 px-2.5 py-2">
          {ListOfMenus.filter((e: any) => e._stickyBottom).map((e: any) => (
            <AppSubmenu
              data={e}
              key={`${e?.pageName}-${e?.key}`}
              collapsed={!expanded}
              secondaryOpen={secondaryMenu?.key === e?.key}
              onMenuClick={handleMenuClick}
            />
          ))}
        </div>

        <div className="shrink-0 px-3 pb-4 pt-1">
          <button
            type="button"
            title={expanded ? "Collapse sidebar" : "Expand sidebar"}
            onClick={() => setOpen(!expanded)}
            className="mx-auto flex h-8 w-8 items-center justify-center rounded-md text-white/70 transition hover:bg-white/10 hover:text-white"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className={`transition-transform ${expanded ? "" : "rotate-180"}`}
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        </div>
      </aside>

      {secondaryOpen ? (
        <LeadsSecondaryPanel
          menu={secondaryMenu}
          leftOffset={primaryWidth}
          width={subMenuWidth}
        />
      ) : null}
    </nav>
  );
}
